import { IPopupHint } from "@cak-legacy/shared-common";
import * as React from "react";
import { HintPopupContext } from "../Context/HintPopupContextProvider";
import { translate } from "../Locales/translator";
import { getOffset } from "../Utils/element-offset";
import "./HintPopup.scss";

export interface IHintPopupProps {
  popupHint: IPopupHint;
  fieldId: string;
}

export const HintPopup = (hintProps: IHintPopupProps) => {
  const [currentHintPopup, setCurrentHintPopup] = React.useContext(HintPopupContext);
  const popupTarget = React.useRef<HTMLButtonElement | null>(null);
  const popupOverlay = React.useRef(null);
  const [showHintPopup, setShowHintPopup] = React.useState(false);
  const [currentPopupTrigger, setCurrentPopupTrigger] = React.useState("");
  const mouseOverOverlayRef = React.useRef(false);

  const hidePopup = () => {
    setShowHintPopup(false);
    setCurrentPopupTrigger("");
    mouseOverOverlayRef.current = false;
    if (currentHintPopup === hintProps.fieldId) {
      setCurrentHintPopup("");
    }
  };

  const onBodyClick = () => {
    hidePopup();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      hidePopup();
    }
  };

  const resizeAndRepositionPopup = () => {
    if (hintProps.popupHint.text || hintProps.popupHint.content) {
      const element = document.getElementById(`${hintProps.fieldId}ToolTip`);
      const bodyElement = document.querySelector("body");
      const arrowElement = document.querySelector(
        `[id="${hintProps.fieldId}ToolTip"] .input__popup-hint-arrow`
      ) as HTMLElement;
      if (
        popupTarget.current &&
        popupOverlay.current &&
        element &&
        bodyElement &&
        arrowElement &&
        showHintPopup
      ) {
        (element.style.right as any) = null;
        (element.style.left as any) = null;
        (element.style.transform as any) = null;
        (arrowElement.style.left as any) = null;
        (arrowElement.style.right as any) = null;

        const box = element.getBoundingClientRect();
        const popupOverlayClientRect = (popupOverlay.current as HTMLElement).getBoundingClientRect();
        const offset = getOffset(popupOverlay.current as HTMLElement);
        const offsetLeft = offset.offsetLeft;

        const offsetIcon = getOffset(popupTarget.current as HTMLElement);

        if (offsetLeft - box.width / 2 < 0) {
          element.style.left = `${-offsetIcon.offsetLeft + 20}px`;
          element.style.right = "auto";
          element.style.transform = "none";

          (arrowElement.style.right as any) = null;
          arrowElement.style.left = offsetIcon.offsetLeft + "px";
        } else if (offsetLeft + box.width / 2 > bodyElement.clientWidth - 20) {
          const arrowClientRect = arrowElement.getBoundingClientRect();

          element.style.right = `${-(
            bodyElement.clientWidth -
            offsetLeft -
            popupOverlayClientRect.width -
            20
          )}px`;
          element.style.left = "auto";
          element.style.transform = "none";

          arrowElement.style.left = "auto";
          arrowElement.style.right = `${
            bodyElement.clientWidth -
            offsetLeft -
            popupOverlayClientRect.width -
            20 +
            popupOverlayClientRect.width / 2 -
            arrowClientRect.width / 2
          }px`;
        } else {
          (element.style.right as any) = null;
          (element.style.left as any) = null;
          (element.style.transform as any) = null;
          (arrowElement.style.left as any) = null;
          (arrowElement.style.right as any) = null;
        }
      }
    }
  };

  const resize = () => {
    hidePopup();
  };

  React.useEffect(() => {
    document.body.addEventListener("click", onBodyClick, false);
    document.addEventListener("keydown", onKeyDown, false);
    window.addEventListener("resize", resize);
    return () => {
      document.body.removeEventListener("click", onBodyClick, false);
      document.removeEventListener("keydown", onKeyDown, false);
      window.removeEventListener("resize", resize);
    };
  }, []);

  React.useEffect(() => {
    if (currentHintPopup !== "" && currentHintPopup !== hintProps.fieldId) {
      hidePopup();
    }
  }, [currentHintPopup]);

  let popupHintCssClass = hintProps.popupHint.icon && ` icon ${hintProps.popupHint.icon}`;
  const onMouseHoverOrFocus = (event) => {
    if ((event.target as any).tagName === "A") {
      return;
    }

    if (currentPopupTrigger === "") {
      mouseOverOverlayRef.current = true;
      setCurrentHintPopup(hintProps.fieldId);
      setShowHintPopup(true);
      setCurrentPopupTrigger("hover");
    }
  };

  const onFocus = (event) => {
    if ((event.target as any).tagName === "A") {
      return;
    }
    setCurrentHintPopup(hintProps.fieldId);
    mouseOverOverlayRef.current = true;
    setShowHintPopup(true);
    setCurrentPopupTrigger("focus");
  };

  const onMouseLeave = (event) => {
    if (currentPopupTrigger === "hover" && event.target.classList.contains("input__popup-hint-overlay")) {
      hidePopup();
      return;
    }

    if (mouseOverOverlayRef.current) {
      return;
    }

    if (currentPopupTrigger === "hover") {
      hidePopup();
    }
  };

  const onBlur = (event) => {
    if (currentPopupTrigger === "hover") {
      hidePopup();
      return;
    }

    if (
      event.relatedTarget &&
      event.relatedTarget.classList &&
      !event.relatedTarget.classList.contains("input__info-hint") &&
      ((event.relatedTarget.closest && !event.relatedTarget.closest(".input__info-hint")) ||
        !event.relatedTarget.closest)
    ) {
      hidePopup();
      return;
    }

    if (event.target.classList && !event.target.classList.contains("input__info-hint")) {
      hidePopup();
    }
  };

  const onFocusOverOverlay = (event) => {
    if ((event.target as any).tagName === "A") {
      return;
    }
    mouseOverOverlayRef.current = true;
  };

  const onMouseOverOverlay = (_event) => {
    mouseOverOverlayRef.current = true;
  };

  const onMouseLeaveOverlay = (_event) => {
    mouseOverOverlayRef.current = false;
  };

  React.useLayoutEffect(() => {
    if (hintProps.popupHint.text || hintProps.popupHint.content) {
      const element = document.getElementById(`${hintProps.fieldId}ToolTip`);
      const bodyElement = document.querySelector("body");
      const arrowElement = document.querySelector(
        `[id="${hintProps.fieldId}ToolTip"] .input__popup-hint-arrow`
      ) as HTMLElement;
      if (
        popupTarget.current &&
        popupOverlay.current &&
        element &&
        bodyElement &&
        arrowElement &&
        showHintPopup
      ) {
        const box = element.getBoundingClientRect();
        const offset = getOffset(popupOverlay.current as HTMLElement);
        const offsetTop = offset.offsetTop;

        let topPositionForCompare = 0;
        if (hintProps.popupHint.querySelectorForElementOffset) {
          const compareElement = document.querySelector(hintProps.popupHint.querySelectorForElementOffset);
          if (compareElement) {
            const offset = getOffset(compareElement as HTMLElement);
            topPositionForCompare = offset.offsetTop;
          }
        }

        if (offsetTop - box.height < topPositionForCompare) {
          popupHintCssClass += " input__info-hint--attach-bottom";

          if (!popupTarget.current.classList.contains("input__info-hint--attach-bottom")) {
            popupTarget.current.classList.add("input__info-hint--attach-bottom");
          }
        } else {
          popupTarget.current.classList.remove("input__info-hint--attach-bottom");
        }
        resizeAndRepositionPopup();
      }

      if (popupTarget.current) {
        if (showHintPopup) {
          if (!popupTarget.current.classList.contains("input__info-hint--z-index-top")) {
            popupTarget.current.classList.add("input__info-hint--z-index-top");
          }
        } else {
          popupTarget.current.classList.remove("input__info-hint--z-index-top");
        }
      }
    }
  });

  return (
    <>
      {(hintProps.popupHint.text || hintProps.popupHint.content) && (
        <button
          ref={popupTarget}
          onClick={(event) => {
            if ((event.target as any).tagName === "A") {
              return;
            }
            event.preventDefault();
            event.stopPropagation();
            const showPopup = !showHintPopup || currentPopupTrigger === "hover";
            setShowHintPopup(showPopup);
            setCurrentPopupTrigger(showPopup ? "click" : "");
            return false;
          }}
          aria-describedby={`${hintProps.fieldId}ToolTip`}
          aria-label={translate("Tooltip_MeerInformatie")}
          onMouseOver={onMouseHoverOrFocus}
          onMouseLeave={onMouseLeave}
          onFocus={onFocus}
          onBlur={onBlur}
          className={`input__info-hint ${popupHintCssClass}`}
          type="button"
        >
          {!hintProps.popupHint.icon && "i"}
          {(hintProps.popupHint.text || hintProps.popupHint.content) && (
            <div
              ref={popupOverlay}
              className={`input__popup-hint-overlay ${
                showHintPopup ? "" : "input__popup-hint-overlay--hidden"
              }`}
            >
              <div
                id={`${hintProps.fieldId}ToolTip`}
                onMouseOver={onMouseOverOverlay}
                onMouseLeave={onMouseLeaveOverlay}
                onFocus={onFocusOverOverlay}
                onClick={(event) => {
                  if ((event.target as any).tagName === "A") {
                    return;
                  }
                  event.preventDefault();
                  return false;
                }}
                className="input__popup-hint"
              >
                {hintProps.popupHint.content ? hintProps.popupHint.content : hintProps.popupHint.text}
                <div className="input__popup-hint-arrow"></div>
              </div>
            </div>
          )}
        </button>
      )}
    </>
  );
};
